import jsPDF from 'jspdf';
import Swal from 'sweetalert2';

/**
 * Genera un REMITO en PDF con formato duplicado (Original y Duplicado para Transporte)
 * Basado en el formato de remito de DORMO HOGAR
 * @param {Object} order - Objeto con los datos del pedido
 * @param {Object} companySettings - Configuración de la empresa (opcional)
 * @returns {Promise<boolean>} - Retorna true si se generó, false si se canceló
 */
export const generateRemitoPDF = async (order, companySettings = null) => {
  // Valores por defecto si no se proporcionan configuraciones
  const defaultSettings = {
    company_name: 'DORMO HOGAR',
    company_cuit: '27-35573639-9',
    company_address: 'Villa del Mar 5010 - Córdoba Capital',
    company_email: 'dormo.online@gmail.com',
    company_phone: '3515081331',
    remito_footer_text: 'ORIGINAL / DUPLICADO PARA TRANSPORTE'
  };
  
  const settings = companySettings || defaultSettings;
  
  // Mostrar confirmación antes de generar
  const result = await Swal.fire({
    title: '¿Generar Remito del pedido?',
    html: `
      <p>Se generará un remito duplicado (Original + Duplicado para Transporte)</p>
      <p class="text-muted mb-0">Pedido <strong>#${order.order_id}</strong></p>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#6c757d',
    confirmButtonText: '<i class="fas fa-download me-2"></i>Generar Remito',
    cancelButtonText: '<i class="fas fa-times me-2"></i>Cancelar',
    customClass: {
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary'
    }
  });

  if (!result.isConfirmed) {
    return false;
  }

  // Mostrar loading mientras se genera
  Swal.fire({
    title: 'Generando Remito...',
    html: 'Por favor espera un momento',
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });

  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Función para dibujar un remito (se llamará 2 veces: Original y Duplicado)
    const drawRemito = (yStart, tipo) => {
      let y = yStart;
      const leftMargin = 15;
      const rightMargin = 195;
      const lineHeight = 6;

      // ============================================
      // ENCABEZADO
      // ============================================
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      
    
      // TÍTULO PRINCIPAL
      doc.setFontSize(14);
      doc.text(`REMITO // ${settings.company_name}`, leftMargin, y);
      y += lineHeight;

      // Razón Social y CUIT
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(`Razón Social: ${settings.company_name}`, leftMargin, y);
      y += 4;
      doc.text(`CUIT: ${settings.company_cuit}`, leftMargin, y);
      y += lineHeight;

      // ============================================
      // DESTINATARIO
      // ============================================
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('DESTINATARIO', leftMargin, y);
      y += 5;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      
      // Nombre y DNI del cliente
      const clientName = order.client_name || 'Cliente directo';
      const clientDNI = order.client_dni || 'N/A';
      doc.text(`${clientName}`, leftMargin, y);
      doc.text(`DNI: ${clientDNI}`, rightMargin - 30, y, { align: 'right' });
      y += 5;

      // Domicilio
      const address = order.client_address || 'No especificado';
      doc.text(`DOMICILIO: ${address}`, leftMargin, y);
      y += 5;

      // Localidad/Provincia
      const city = order.customer_city || 'No especificado';
      const province = order.customer_province || 'No especificado';
      doc.text(`LOCALIDAD/PROVINCIA: ${province} - ${city}`, leftMargin, y);
      y += 5;

      // Teléfono
      const phone = order.client_cellphone || 'No disponible';
      doc.text(`TELÉFONO: ${phone}`, leftMargin, y);
      y += lineHeight + 2;

      // ============================================
      // REMITENTE (DORMO HOGAR)
      // ============================================
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('REMITENTE', leftMargin, y);
      y += 5;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      
      // Nombre del vendedor/consultor
      const sellerName = order.seller_name || settings.company_name;
      doc.text(`CONSULTOR/VENTA: ${sellerName}`, leftMargin, y);
      y += 5;

      // Dirección DORMO
      doc.text(`DIRECCION: ${settings.company_address}`, leftMargin, y);
      y += 5;

      // Contacto DORMO
      doc.text(`CONTACTO: ${settings.company_email}`, leftMargin, y);
      doc.text(`Tel: ${settings.company_phone}`, rightMargin - 30, y, { align: 'right' });
      y += lineHeight + 2;

      // ============================================
      // CANTIDAD DE BULTOS
      // ============================================
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('CANT BULTOS:', leftMargin, y);
      y += 5;

      // Espacio en blanco para llenar a mano
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      doc.line(leftMargin + 25, y - 2, leftMargin + 60, y - 2); // Línea para escribir
      y += lineHeight;

      // ============================================
      // PRODUCTOS
      // ============================================
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('PRODUCTO/S:', leftMargin, y);
      y += 5;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(7);
      
      // Listar productos
      if (order.items && order.items.length > 0) {
        order.items.forEach((item, index) => {
          const hasSku = item.sku_id && item.sku_id !== null;
          const hasStockOnly = !hasSku && item.stock_products && item.stock_products.length > 0;
          
          // Si es SOLO stock local (sin SKU), no mostrar línea principal
          if (hasStockOnly) {
            // Solo mostrar productos de stock
            item.stock_products.forEach(sp => {
              let stockLine = `• ${sp.product_name}`;
              if (sp.quantity > 1) {
                stockLine += ` (x${sp.quantity})`;
              }
              stockLine += ' [Stock Local]';
              
              doc.text(stockLine, leftMargin + 3, y);
              y += 4;
            });
            return; // Salir, no mostrar detalles de SKU
          }
          
          // Item normal con SKU
          const productName = item.sku_description || `Producto ${item.sku || 'N/A'}`;
          const quantity = item.quantity || 0;
          const material = item.details?.material_name || '';
          const measurements = item.details?.measurements || '';
          
          // Línea del producto con viñeta
          let productLine = `• ${productName}`;
          if (quantity > 1) {
            productLine += ` (x${quantity})`;
          }
          
          doc.text(productLine, leftMargin + 3, y);
          y += 4;
          
          // Detalles adicionales en línea siguiente
          if (material || measurements) {
            let details = '  ';
            if (material) details += `Material: ${material}`;
            if (measurements) details += ` | Medidas: ${measurements}`;
            doc.setFontSize(6);
            doc.text(details, leftMargin + 5, y);
            doc.setFontSize(7);
            y += 4;
          }
          
          // Colores si hay
          if (item.details?.colors && item.details.colors.length > 0) {
            const colors = item.details.colors.map(c => c.color_name).join(', ');
            doc.setFontSize(6);
            doc.text(`  Colores: ${colors}`, leftMargin + 5, y);
            doc.setFontSize(7);
            y += 4;
          }
          
          // PRODUCTOS DE STOCK LOCAL (adicionales al SKU)
          if (item.stock_products && item.stock_products.length > 0) {
            item.stock_products.forEach(sp => {
              let stockLine = `  ◦ ${sp.product_name}`;
              if (sp.quantity > 1) {
                stockLine += ` (x${sp.quantity})`;
              }
              stockLine += ' [Stock Local]';
              
              doc.setFontSize(6);
              doc.text(stockLine, leftMargin + 5, y);
              doc.setFontSize(7);
              y += 4;
            });
          }
        });
      } else {
        doc.text('Sin productos', leftMargin + 3, y);
        y += 5;
      }

      y += 3;

      // ============================================
      // VALOR DECLARADO (espacio en blanco)
      // ============================================
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.text('VALOR DECLARADO:', leftMargin, y);
      y += 5;
      doc.setFont(undefined, 'normal');
      doc.line(leftMargin + 35, y - 2, leftMargin + 80, y - 2);
      y += lineHeight;

      // ============================================
      // PIE DE PÁGINA
      // ============================================
      // Línea de separación
      y += 2;
      doc.line(leftMargin, y, rightMargin, y);
      y += 5;

      // Original o Duplicado
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.text(tipo, leftMargin, y);
      
      // Firma del transportista
      doc.text('FIRMA TRANSPORTISTA', rightMargin - 45, y, { align: 'right' });
      
      // Línea para firma
      doc.setFont(undefined, 'normal');
      doc.line(rightMargin - 43, y + 8, rightMargin, y + 8);
    };

    // ============================================
    // GENERAR DOS REMITOS EN LA MISMA HOJA
    // ============================================
    
    // REMITO 1: ORIGINAL (Parte superior)
    drawRemito(15, 'ORIGINAL');
    
    // Línea de corte en el medio
    doc.setLineDash([3, 3]);
    doc.line(10, 148.5, 200, 148.5);
    doc.setLineDash([]);
    
    // REMITO 2: DUPLICADO PARA TRANSPORTE (Parte inferior)
    drawRemito(153, 'DUPLICADO PARA TRANSPORTE');

    // Guardar el PDF
    const fileName = `Remito_Pedido_${order.order_id}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    // Cerrar el loading y mostrar éxito
    Swal.fire({
      title: '¡Remito generado!',
      text: 'El archivo se ha descargado correctamente',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });

    return true;

  } catch (error) {
    console.error('Error generando remito:', error);
    Swal.fire({
      title: 'Error',
      text: 'Hubo un problema al generar el remito. Por favor intenta nuevamente.',
      icon: 'error',
      confirmButtonText: 'Entendido'
    });
    return false;
  }
};
